import { z } from 'zod';

import { telegramIdSchema } from './common';

/**
 * «Писал ли этот человек в поддержку?» — единственный вопрос, который проект
 * задаёт платформе о контакте, и задаёт он его сам (pull), а не ждёт от нас
 * события.
 *
 * Так сделано потому, что вебхук саппорт-бота принадлежит платформе: Telegram
 * отдаёт апдейты бота ровно на один URL, и проект физически не может узнать
 * о сообщении иначе, чем спросив у нас. Обратное направление (мы уведомляем
 * проект) потребовало бы от него принимать и хранить наши события ради факта,
 * который у нас и так есть.
 *
 * Вопрос ставится пачкой: проект спрашивает про одного человека в момент, когда
 * тот жмёт «Перевірити», и про сотню — когда сверяет задание фоном.
 */

/** Потолок пачки. Ответ читается из индексов, но это всё же чужой запрос. */
export const CONTACTS_CHECK_MAX_IDS = 100;

export const contactsCheckSchema = z
  .object({
    telegram_id: telegramIdSchema.optional(),
    telegram_ids: z.array(telegramIdSchema).max(CONTACTS_CHECK_MAX_IDS).optional(),
  })
  .refine(
    (value) => value.telegram_id !== undefined || (value.telegram_ids?.length ?? 0) > 0,
    { error: 'Нужен telegram_id или непустой telegram_ids', path: ['telegram_id'] },
  );
export type ContactsCheckRequest = z.infer<typeof contactsCheckSchema>;

/**
 * Ответ на одного человека.
 *
 * Два разных признака, и путать их нельзя. `contacted` — контакт вообще
 * существует: человек открыл бота и нажал «Старт», не написав ни слова.
 * `messages_in` — сколько он реально написал. Проект сам решает, что считать
 * выполнением задания: тап по кнопке или живое обращение.
 */
export interface ContactCheckResult {
  telegram_id: number;
  contacted: boolean;
  /** Входящих сообщений от человека (0 — только нажал «Старт»). */
  messages_in: number;
  /** ISO-8601 первого входящего сообщения, null — писем не было. */
  first_message_at: string | null;
  last_message_at: string | null;
}

export interface ContactsCheckResponse {
  ok: true;
  contacts: ContactCheckResult[];
}
